import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, setDoc } from 'firebase/firestore';
import { db } from '../../../../firebase/config';
import emailChanges from '../../../../data/emailChanges';
import { convertObjectDates } from '../../../../utils/firebase-timestamp-transformation';
import './MyelinersTab.scss';

const MyelinersTab = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [migrating, setMigrating] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const myelinersRef = collection(db, 'myeliners');
      // No filters for now, get all myeliners
      const querySnapshot = await getDocs(myelinersRef);
      
      const employeesList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setEmployees(employeesList);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching myeliners:', error);
      setLoading(false);
    }
  };

  // Function to find new email for an employee from emailChanges.js
  const findNewEmail = (oldEmail) => {
    const change = emailChanges.find(change => change.oldEmail === oldEmail);
    return change ? change.newEmail : null;
  };

  // Handle migrate button click
  const handleMigrate = async (employee) => {
    const oldEmail = employee.email;
    const newEmail = findNewEmail(oldEmail);
    
    if (!newEmail) {
      alert(`No migration mapping found for ${oldEmail} in emailChanges.js`);
      return;
    }
    
    if (!window.confirm(`Are you sure you want to migrate ${oldEmail} to ${newEmail}?`)) {
      return;
    }
    
    try {
      setMigrating(employee.email);
      
      // Check if an employee with the new email already exists
      const myelinersRef = collection(db, 'myeliners');
      const newEmailQuery = query(myelinersRef, where('email', '==', newEmail));
      const newEmailSnapshot = await getDocs(newEmailQuery);
      
      if (!newEmailSnapshot.empty) {
        alert(`An employee with email ${newEmail} already exists in the database.`);
        setMigrating(null);
        return;
      }
      
      // Convert any timestamp fields to proper JavaScript Date objects
      const processedEmployee = convertObjectDates(employee);
      
      // Create new employee document with updated email
      const newEmployeeData = {
        ...processedEmployee,
        email: newEmail,
        oldEmail: oldEmail,
        migrated: true,
        migratedAt: new Date()
      };
      
      // Use the new email as the document ID
      const newEmployeeRef = doc(db, 'myeliners', newEmail);
      await setDoc(newEmployeeRef, newEmployeeData);
      
      alert(`Successfully migrated ${oldEmail} to ${newEmail}`);
      
      // Refresh the list
      fetchEmployees();
      
    } catch (error) {
      console.error('Error migrating employee:', error);
      alert(`Error migrating employee: ${error.message}`);
    } finally {
      setMigrating(null);
    }
  };

  const filteredEmployees = searchTerm 
    ? employees.filter(emp => 
        emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.designation?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : employees;

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // If it's a timestamp (has seconds property)
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString();
    }
    
    // If it's already a Date object
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    
    // Try to parse as date string
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="myeliners-tab">
      <div className="myeliners-header">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="refresh-container">
          <button 
            className="refresh-button"
            onClick={fetchEmployees}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading employees...</p>
        </div>
      ) : filteredEmployees.length > 0 ? (
        <div className="myeliners-table-container">
          <table className="myeliners-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Department</th>
                <th>Designation</th>
                <th>Join Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map(employee => (
                <tr key={employee.id} className={employee.migrated ? 'migrated-row' : ''}>
                  <td>{employee.name || 'N/A'}</td>
                  <td>
                    {employee.email}
                    {employee.migrated && <span className="migrated-badge">Migrated</span>}
                  </td>
                  <td>{employee.department || 'N/A'}</td>
                  <td>{employee.designation || 'N/A'}</td>
                  <td>{formatDate(employee.doj)}</td>
                  <td>
                    {!employee.migrated && findNewEmail(employee.email) && (
                      <button
                        className="migrate-button"
                        onClick={() => handleMigrate(employee)}
                        disabled={migrating === employee.email}
                      >
                        {migrating === employee.email ? 'Migrating...' : 'Migrate'}
                      </button>
                    )}
                    {employee.migrated && (
                      <span className="already-migrated">Already Migrated</span>
                    )}
                    {!findNewEmail(employee.email) && !employee.migrated && (
                      <span className="no-mapping">No Mapping</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">
          <p>No employees found.</p>
        </div>
      )}
    </div>
  );
};

export default MyelinersTab; 